import type { LoadPhases, RestorePhase } from "../storage/loadHint";
import { RESTORE_PHASES } from "../storage/loadHint";

/** Where the bar goes, in CSS pixels: on the mat, relative to the canvas
 *  host, just below the grid frame's top line from its left corner to its
 *  right one; with the mat hidden, relative to the viewport, along the bottom
 *  of the top bar. */
export interface BarPlace {
  left: number;
  width: number;
  /** Bottom edge of the grid frame's top line. */
  top: number;
  /** The bar's thickness. */
  height: number;
  /** Whether it sits on the mat, showing through the strip the mat leaves
   *  open. Off the mat it moves out of the canvas host to run under the top
   *  bar, with no track of its own. */
  onMat: boolean;
}

/** A point on the bar's animation: `at` is the share of its duration (0..1),
 *  `value` the share of the bar filled. */
interface Point {
  at: number;
  value: number;
}

/** Ease-out for the no-prediction case, as linear segments: fast at first,
 *  slowing toward 90%. */
const EASE_OUT: Point[] = [
  { at: 0, value: 0 },
  { at: 0.15, value: 0.4 },
  { at: 0.35, value: 0.65 },
  { at: 0.6, value: 0.8 },
  { at: 1, value: 0.9 },
];

/** The bar stops just short of the end until the project actually arrives,
 *  so a load that runs past its prediction never looks finished. */
const HOLD_AT = 0.97;
/** How long the bar takes to fill the rest of the way once it's done. */
const FILL_MS = 150;

/**
 * The delayed-load progress bar (#1230): a DOM element behind the canvas,
 * showing through a strip the mat leaves open just below the grid's top
 * line, so the grid lines and ticks draw over it. It fills with a
 * `transform: scaleX()` animation. A transform animation runs on the compositor, so the bar keeps
 * moving while parsing or rendering blocks the main thread.
 *
 * Progress is time-based over the restore: the bar shows when the mat is first
 * drawn (the earliest its dimensions are known), starts at 0, and runs
 * linearly to the restore's predicted end. Startup before that is covered by
 * "Loading…" in the title, not the bar, which nobody would have seen fill. At
 * each restore phase's end it re-anchors on the real clock:
 * it never moves backwards, and from where it is it runs to the end of the
 * phases still to come at their predicted lengths, so one phase's error
 * doesn't carry into the next. With no prediction (the first timed load on
 * this device) it eases toward the end instead.
 */
export class LoadProgressBar {
  private animation: Animation | null = null;
  private frame = 0;
  /** The highest fraction the bar has been told to show. It only grows: every
   *  animation starts at or above it, so the bar never moves backwards. */
  private shown = 0;
  /** The running animation as piecewise-linear points on the real clock
   *  (`performance.now()`). The bar's position is computed from it, never
   *  read back from the rendered style: the animation timeline only advances
   *  when a frame is drawn, and the restore's yields don't necessarily draw
   *  one, so the style can lag what the compositor shows. Every curve is
   *  linear segments, so this computation is exact. */
  private plan: { start: number; duration: number; points: Point[] } = {
    start: 0,
    duration: 1,
    points: [
      { at: 0, value: 0 },
      { at: 1, value: 0 },
    ],
  };
  private remaining: RestorePhase[] = [...RESTORE_PHASES];
  /** Where the track lives while it's on the mat: the canvas host. */
  private matHome: HTMLElement | null;

  /**
   * @param track the bar's track, behind the canvas
   * @param el the bar inside it
   * @param place where the track goes, or null when there's no canvas to
   *   put it on
   * @param strip opens or closes the strip in the mat that the bar shows
   *   through
   * @param restoreStart when the restore began (`performance.now()`)
   */
  constructor(
    private track: HTMLElement,
    private el: HTMLElement,
    private place: () => BarPlace | null,
    private predicted: LoadPhases | null,
    private strip: (open: boolean) => void,
    private restoreStart = performance.now(),
  ) {
    this.matHome = track.parentElement;
  }

  /** Show the bar and start it running. */
  start(): void {
    this.strip(true);
    this.track.hidden = false;
    this.follow();
    if (!this.predicted) {
      // No prediction (the first timed load on this device): ease toward the
      // end, fast then slower, as linear segments so its position is known.
      this.animateTo(EASE_OUT, 2000);
      return;
    }
    // From 0, over what's left of the predicted restore: all of it when the
    // bar shows as the load starts, less what has passed when a load
    // predicted fast runs over and the bar shows late.
    this.runToEnd(performance.now() - this.restoreStart);
  }

  /** A restore phase just finished: re-anchor on the real clock. */
  phaseDone(phase: RestorePhase): void {
    this.remaining = this.remaining.slice(this.remaining.indexOf(phase) + 1);
    if (!this.animation || !this.predicted) return;
    this.runToEnd();
  }

  /** The project is in: fill to the end, then hide. */
  finish(): void {
    const fill = this.animateTo(
      [
        { at: 0, value: 0 },
        { at: 1, value: 1 },
      ],
      FILL_MS,
    );
    fill.onfinish = () => this.hide();
  }

  /** Hide the bar at once (nothing was loaded after all). */
  hide(): void {
    cancelAnimationFrame(this.frame);
    this.animation?.cancel();
    this.animation = null;
    this.track.hidden = true;
    this.strip(false);
  }

  /** From where the bar is, run to HOLD_AT over the predicted length of the
   *  phases still to come, less `elapsed` ms already spent in them. */
  private runToEnd(elapsed = 0): void {
    const left =
      this.remaining.reduce((s, k) => s + this.predicted![k], 0) - elapsed;
    // Spread over what's left; if nothing is predicted to be left, hold.
    this.animateTo(
      [
        { at: 0, value: 0 },
        { at: 1, value: HOLD_AT },
      ],
      Math.max(left, 1),
    );
  }

  /**
   * Replace the running animation with `shape` over `duration` ms, starting
   * from where the bar is now. `shape`'s values are rescaled to run from the
   * current position to the shape's end, and never below what has been
   * shown, so the bar only moves forward.
   */
  private animateTo(shape: Point[], duration: number): Animation {
    const from = Math.max(this.current(), this.shown);
    const end = Math.max(shape[shape.length - 1]!.value, from);
    const span = shape[shape.length - 1]!.value - shape[0]!.value || 1;
    const points = shape.map((p) => ({
      at: p.at,
      value: from + ((p.value - shape[0]!.value) / span) * (end - from),
    }));
    // The bar's own style holds the start value, so even a frame between the
    // old animation ending and the new one starting can't show less.
    this.el.style.transform = `scaleX(${from})`;
    this.shown = from;
    this.animation?.cancel();
    this.plan = { start: performance.now(), duration, points };
    this.animation = this.el.animate(
      points.map((p) => ({
        offset: p.at,
        transform: `scaleX(${p.value})`,
      })),
      { duration, easing: "linear", fill: "forwards" },
    );
    return this.animation;
  }

  /** The fraction the bar shows right now, from the plan and the real
   *  clock. */
  private current(): number {
    const { start, duration, points } = this.plan;
    const t = Math.min(Math.max((performance.now() - start) / duration, 0), 1);
    let i = 1;
    while (i < points.length - 1 && points[i]!.at < t) i++;
    const a = points[i - 1]!;
    const b = points[i]!;
    const k = b.at > a.at ? (t - a.at) / (b.at - a.at) : 1;
    return Math.max(a.value + (b.value - a.value) * k, this.shown);
  }

  /** Keep the bar on the grid line while the view moves (a pan or zoom
   *  during the load), or under the top bar while the mat is hidden. Runs
   *  only while the bar shows. */
  private follow = (): void => {
    const at = this.place();
    this.track.style.visibility = at ? "" : "hidden";
    if (at) {
      const home = at.onMat ? this.matHome : document.body;
      if (home && this.track.parentElement !== home) home.append(this.track);
      this.track.classList.toggle("off-mat", !at.onMat);
      this.track.style.left = `${at.left}px`;
      this.track.style.width = `${at.width}px`;
      this.track.style.top = `${at.top}px`;
      this.track.style.height = `${at.height}px`;
    }
    this.frame = requestAnimationFrame(this.follow);
  };
}
